<template>
  <div class="rn-slider-area" id="home" v-if="NOTARIA && NOTARIA.Theme">
    <div class="slider-style-dynamic bg_image">
      <!-- Background Image with Filter (isolated so it doesn't darken text) -->
      <div class="bg_media_dynamic" :style="backgroundMediaStyle"></div>

      <!-- Video Background -->
      <video v-if="file && file.type === 'video/mp4'" class="home-video" :src="imagenIDtoSRC(style.ThemeHomeBanner.Imagen)"
             preload="metadata" muted autoplay playsinline loop />

      <v-container class="position-relative z-index-10">
        <v-row align="center" justify="center">
          <v-col cols="12" class="text-center">
            <div class="banner-home-dynamic inner">
              <!-- Animated Logo -->
              <div class="logo-wrapper-dynamic mb--40" v-if="NOTARIA.Theme.Home.Banner.Logo && !ModoAccesible" :style="logoBlockStyle">
                <img class="img-fluid logo-premium" :style="logoImgStyle" 
                    :src="NOTARIA?.Theme?.Home?.Banner?.ImagenLogo?.trim() ? imagenIDtoSRC(NOTARIA.Theme.Home.Banner.ImagenLogo) : '/img/logo-notariado.png'"
                    :alt="'Logo de la ' + NOTARIA.Nombre"
                />
              </div>

              <!-- Main Titles with Modern Shadow -->
              <h1 class="heading-dynamic mb--20" :style="tituloStyle" v-html="NOTARIA.Theme.Home.Banner.Titulo.Texto"></h1>
              
              <div class="sub-wrapper mb--30">
                <h2 class="subheading-dynamic" :style="subtituloStyle" v-html="NOTARIA.Theme.Home.Banner.Subtitulo.Texto"></h2>
              </div>

              <div v-if="NOTARIA.Theme.Home.Banner.Texto" class="description-dynamic mb--50"
                  :style="textoStyle" v-html="NOTARIA.Theme.Home.Banner.Texto.Texto"></div>

              <!-- Premium Buttons -->
              <div class="slide-btn-dynamic d-flex flex-wrap justify-center gap-20" :style="buttonsBlockStyle">
                <v-btn id="button-menu" 
                       v-if="NOTARIA.Theme.Home.Banner.ShowButtonMoveMenu"  
                       class="rn-btn btn-dynamic-primary" 
                       :style="buttonMenuStyle"  
                       @click="goTo(compMenuAction)">
                  {{ NOTARIA.Theme.Home.Banner.ButtonMoveMenu.Texto }}
                </v-btn>

                <v-btn id="button-inicio" 
                       v-if="NOTARIA.Theme.Home.Banner.ShowButtonMoveInicio" 
                       class="rn-btn btn-dynamic-secondary" 
                       :style="buttonInicioStyle"
                       @click="scrollMeTo(compInicioAction)">
                  {{ NOTARIA.Theme.Home.Banner.ButtonMoveInicio.Texto }}
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
      
      <!-- Bottom Decorative Element -->
      <div v-if="NOTARIA.Theme.Home.Banner.ShowArrow" class="scroll-indicator-dynamic" @click="handleArrowClick">
        <v-icon :color="NOTARIA?.Theme?.General?.TituloCss?.Color || 'white'">mdi-chevron-down</v-icon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from '../../store'
import { useUtils } from '#imports'
import { navigateTo } from '#app/composables/router'
import { TiposAccionBoton } from '~/helpers/Enums/Enum'
import { useStyles } from '../../store/styles'

const store = useStore()
const { NOTARIA, ModoAccesible } = storeToRefs(store)
const style = useStyles()
const { imagenIDtoSRC, getFile } = useUtils()

const file = computed(() => getFile(style.ThemeHomeBanner?.Imagen))
const imgFiltro = computed(() => style.ThemeHomeBanner?.ImagenFiltro ?? '')
const img = computed(() => file.value && file.value.type !== 'video/mp4' ? `url(${imagenIDtoSRC(style.ThemeHomeBanner?.Imagen)})` : '')

const compMenuAction = computed(() =>
  NOTARIA.value?.Theme?.Home?.Banner?.ButtonMoveMenu ? getTipoAccion(NOTARIA.value.Theme.Home.Banner.ButtonMoveMenu.Click) : null
)

const compInicioAction = computed(() =>
  NOTARIA.value?.Theme?.Home?.Banner?.ButtonMoveInicio ? getTipoAccion(NOTARIA.value.Theme.Home.Banner.ButtonMoveInicio.Click) : null
)

const logoBlockStyle = computed(() => ({
  justifyContent: style.ThemeHomeBanner?.ImagenLogoBlock?.css?.Align ?? 'center'
}))

const logoImgStyle = computed(() => ({
  maxWidth: style.ThemeHomeBanner?.ImagenLogoBlock?.css?.Size ?? '150px'
}))

const tituloStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Titulo?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Titulo?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Titulo?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Titulo?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Titulo?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Titulo?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Titulo.css.BackgroundColor}` : ''
}))

const subtituloStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Subtitulo?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Subtitulo?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Subtitulo?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Subtitulo?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Subtitulo?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Subtitulo?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Subtitulo.css.BackgroundColor}` : ''
}))

const textoStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Texto?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Texto?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Texto?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Texto?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Texto?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Texto?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Texto.css.BackgroundColor}` : ''
}))

const buttonsBlockStyle = computed(() => ({
  justifyContent: style.ThemeHomeBanner?.ButtonsBlock?.css?.Align ?? 'center'
}))

const buttonMenuStyle = computed(() => ({
  color: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Font || '',
  backgroundColor: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.BackgroundColor || '',
  fontSize: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Weight || '',
  border: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Align || ''
}))

const buttonInicioStyle = computed(() => ({
  color: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Font || '',
  backgroundColor: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.BackgroundColor || '',
  fontSize: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Weight || '',
  border: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Align || ''
}))

const backgroundMediaStyle = computed(() => ({
  backgroundImage: img.value,
  filter: imgFiltro.value,
  WebkitFilter: imgFiltro.value
}))

function goTo(refName: TiposAccionBoton) {
  if (!refName) return
  if (refName === TiposAccionBoton.NV) {
    navigateTo(NOTARIA.value.NotariaVirtual, { external: true })
  } else {
    navigateTo(refName.resumen)
  }
}

function scrollMeTo(refName: TiposAccionBoton) {
  if (!refName || refName === TiposAccionBoton.undefined) return
  const element = document.getElementById(refName.name) || document.querySelector(refName.name)
  if (element) window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' })
}

function scrollToNextSection() {
  const nextSection = document.querySelector('.home-section');
  if (nextSection) {
    window.scrollTo({
      top: nextSection.offsetTop - 80,
      behavior: 'smooth'
    });
  }
}

function handleArrowClick() {
  const actionName = NOTARIA.value?.Theme?.Home?.Banner?.ArrowAction;
  if (actionName) {
    const action = getTipoAccion(actionName);
    scrollMeTo(action);
  } else {
    scrollToNextSection();
  }
}

function getTipoAccion(value: string) {
  switch (value) {
    case 'about': return TiposAccionBoton.Notario
    case 'service': return TiposAccionBoton.Servicios
    case 'blog': return TiposAccionBoton.Blog
    case 'testimonial': return TiposAccionBoton.Testimonial
    case 'homegallery': return TiposAccionBoton.Gallery
    case 'Inicio': return TiposAccionBoton.Inicio
    case 'contact': return TiposAccionBoton.Contacto
    case 'NV': return TiposAccionBoton.NV
    case 'equipo': return TiposAccionBoton.Equipo
    default: return TiposAccionBoton.undefined
  }
}
</script>

<style scoped lang="scss">
.slider-style-dynamic {
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.bg_media_dynamic {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 0;
}


.home-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.z-index-10 {
  z-index: 10;
}

.logo-premium {
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  transition: transform 0.5s ease;
  &:hover {
    transform: scale(1.05);
  }
}

.heading-dynamic {
  line-height: 1.1;
  letter-spacing: -1px;
  animation: fadeInUp 0.8s ease-out forwards;
}

.subheading-dynamic {
  animation: fadeInUp 0.8s ease-out 0.2s forwards;
  opacity: 0;
}

.description-dynamic {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  animation: fadeInUp 0.8s ease-out 0.4s forwards;
  opacity: 0;
}

.gap-20 {
  gap: 20px;
}

.btn-dynamic-primary, .btn-dynamic-secondary {
  height: 60px;
  padding: 0 40px;
  border-radius: 30px; /* Redondez (se mantiene pero sin important para no bloquear CSS inline si se requiere, aunque el panel no manda radius) */
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: none !important;
  border-width: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* Movimiento */
  animation: fadeInUp 0.8s ease-out 0.6s forwards;
  opacity: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.3);
  }
}

.btn-dynamic-secondary {
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
}

.scroll-indicator-dynamic {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  cursor: pointer;
  animation: bounce 2s infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateX(-50%) translateY(0);}
  40% {transform: translateX(-50%) translateY(-10px);}
  60% {transform: translateX(-50%) translateY(-5px);}
}

@media (max-width: 600px) {
  .heading-dynamic {
    font-size: 2.5rem !important;
  }
  .subheading-dynamic {
    font-size: 1.2rem !important;
  }
  .slide-btn-dynamic {
    flex-direction: column;
    align-items: center;
  }
  .btn-dynamic-primary, .btn-dynamic-secondary {
    width: 100%;
    max-width: 300px;
  }
}
</style>
